<template>
  <template v-if="!loading">
    <router-view></router-view>
    <lightboxes />
    <route-selector-modal />
  </template>

  <loader-spinner v-if="loading" />
  <VueQueryDevtools v-if="isDevEnv" />
</template>

<script>
// libs
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import axios from 'axios';
import axiosDefaults from 'axios/lib/defaults';
import 'bootstrap';
import _ from 'lodash';
import { provide } from 'vue';
import { useToast } from 'vue-toastification';

// helpers
import endpointVersion from '@/helpers/endpoint-version';
import bus from '@/helpers/events/bus';
import { setUserRead } from '@/helpers/warnings';
// views
import Lightboxes from '@/views/Lightboxes';
// components - atoms
import LoaderSpinner from '@/components/_atoms/BaseLoaderSpinner';
import RouteSelectorModal from '@/components/_atoms/routeSelector/RouteSelectorModal';

export default {
  name: 'App',
  title() {
    return 'Oto';
  },
  components: {
    Lightboxes,
    LoaderSpinner,
    VueQueryDevtools,
    RouteSelectorModal,
  },
  setup() {
    provide('languages', ['pt-BR', 'en']);
    const toast = useToast();

    return { toast };
  },
  data() {
    return {
      mfa: 'mfa',
      loading: true,
    };
  },
  computed: {
    getLanguage() {
      return this.$store.getters.user?.language;
    },
    isDevEnv() {
      const hostname = window.location.hostname;
      const allowVueQueryDevTools = ['-dev', 'localhost'];
      return allowVueQueryDevTools.includes(hostname);
    },
  },
  watch: {
    $route(to) {
      this.updatePageTitle(to);

      bus.$emit('dismiss-toasts');
    },
    getLanguage() {
      this.updatePageTitle(this.$route);
    },
  },
  created() {
    this.fetchInterceptors();
    this.loading = false;

    bus.$on('dismiss-toasts', () => {
      this.toast.clear();
    });
    bus.$on('set-baseUrl', () => {
      this.handleBaseUrl();
    });
    bus.$on('start-change-brand', () => {
      this.loading = true;
    });
    bus.$on('finish-change-brand', () => {
      this.loading = false;
    });

    window.addEventListener('keydown', this.handleKeyboardShortcut);
  },
  beforeUnmount() {
    bus.$off('set-baseUrl');
    window.removeEventListener('keydown', this.handleKeyboardShortcut);
  },
  methods: {
    handleKeyboardShortcut(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        bus.$emit('open-route-selector-modal');
      }
    },
    updatePageTitle(route) {
      const isEmptyMeta = !route.meta?.title || Object.keys(route.meta.title).length === 0;

      const hasStore = !!this.$store && this.$store.getters.user;
      this.$i18n.locale = hasStore ? this.$store.getters.user.language : 'pt-BR';

      if (isEmptyMeta) {
        document.title = 'Oto. Quem conhece, vende.';
      } else {
        document.title = `Oto | ${this.$t('header.sidebar.' + route.meta.title)}`;
      }
      bus.$emit('dismiss-toasts');
    },
    fetchInterceptors() {
      axios.interceptors.response.use(
        response => {
          if (_.size(response?.data?.warnings)) {
            const warningsList = response?.data?.warnings;
            _.forIn(warningsList, (message, id) => {
              this.toast.warning(message, {
                id,
                onClick: () => setUserRead(id),
              });
            });
          }

          if (this.hasStatus401(response)) this.dispatchToLogout();

          return response;
        },
        error => {
          if (this.hasStatus401(error.response) && error.response?.data?.errors?.error_id === 'mfa_error') {
            bus.$emit('mfa-change', this.mfa);
            return;
          }
          if (this.hasStatus401(error.response) && error.response.data.errors.error_id === 'mfa_must_validate') {
            bus.$emit('mfa-must-validate');
            return;
          }
          if (this.hasStatus401(error.response)) this.dispatchToLogout();
        },
      );
    },
    hasStatus401(response) {
      return response?.status === 401;
    },
    dispatchToLogout() {
      this.$store.dispatch('logout').then(() => this.$router.push('/login'));
    },
    isAt(route) {
      return this.$router.currentRoute.value.matched.includes(route);
    },
    handleBaseUrl() {
      axiosDefaults.baseURL = endpointVersion();
    },
  },
};
</script>
<style lang="scss">
@import '~bootstrap/scss/bootstrap';
@import '~daterangepicker/daterangepicker.css';
</style>
